import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import HttpService from '../../services/httpService';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    TableFooter,
    TextField,
    Button,
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    IconButton,
    Tooltip,
    Chip,
    CircularProgress,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import moment from 'moment';
import XLSX from 'xlsx';
import PanelDownloadProgressDialog from '../../components/PanelDownloadProgressDialog';
import { downloadDeliveryPackagePanelCsv, saveCsvBlob } from '../../utils/panelDownload';

const PACKAGE_TYPE = {
    MODULAR: 'Modular',
    HARDWARE: 'Hardware',
    BOTH: 'Both',
};

const getDateFilterTypeForTab = (currentTab) => {
    if (currentTab === PACKAGE_TYPE.MODULAR) return PACKAGE_TYPE.MODULAR;
    if (currentTab === PACKAGE_TYPE.HARDWARE) return PACKAGE_TYPE.HARDWARE;
    return null;
};

const PAGE_MODE_CONFIG = {
    full: {
        title: 'Full Package Delivered',
        logLabel: 'FullPackageDelivered',
        getSummary: (body) => HttpService.getDeliveryPackageScanSummary(body),
        getDetails: (body) => HttpService.getDeliveryPackageScanDetails(body),
        getPanelDetails: (body) => HttpService.getDeliveryPackagePanelDetails(body),
        getHardwareItemDetails: (body) => HttpService.getDeliveryPackageHardwareItemDetails(body),
    },
    partial: {
        title: 'Partial Delivery Packages',
        logLabel: 'PartialPackageDelivered',
        getSummary: (body) => HttpService.getDeliveryPackagePartialScanSummary(body),
        getDetails: (body) => HttpService.getDeliveryPackagePartialScanDetails(body),
        getPanelDetails: (body) => HttpService.getDeliveryPackagePartialPanelDetails(body),
        getHardwareItemDetails: (body) => HttpService.getDeliveryPackagePartialHardwareItemDetails(body),
    },
    both: {
        title: 'Partial & Full Delivery Reports',
        logLabel: 'PanelDeliveryReportsBoth',
        getSummary: (body) => HttpService.getDeliveryPackageCombinedScanSummary(body),
        getDetails: (body) => HttpService.getDeliveryPackageCombinedScanDetails(body),
        getPanelDetails: (body) => HttpService.getDeliveryPackageCombinedPanelDetails(body),
        getHardwareItemDetails: (body) => HttpService.getDeliveryPackageCombinedHardwareItemDetails(body),
    },
};

const DEFAULT_PAGE_SIZE = 250;
const PANEL_PREVIEW_LIMIT = 500;
const DEFAULT_DATE_RANGE_DAYS = 30;
const FETCH_NEW_DATA_COOLDOWN_MS = 3 * 60 * 1000;
const FETCH_NEW_DATA_COOLDOWN_SECONDS = 180;

const getFetchNewDataDisableUntilKey = (mode) => `panelDeliveryReports_${mode}_fetchNewData_disableUntil`;

const getDeliveryTypeLabel = (row) => {
    if (row?.deliveryType) return row.deliveryType;
    if (row?._deliverySource === 'full') return 'Full';
    if (row?._deliverySource === 'partial') return 'Partial';
    return '-';
};

const resolveRowApiConfig = (row, mode, pageConfig) => {
    const source = row?._deliverySource || mode;
    return PAGE_MODE_CONFIG[source] || pageConfig;
};

const appendDeliverySourceToBody = (body, row, mode) => {
    if (mode !== 'both') return body;
    return {
        ...body,
        deliverySource: row?._deliverySource || 'partial',
    };
};

const getDefaultDateRange = () => ({
    startDate: moment().subtract(DEFAULT_DATE_RANGE_DAYS, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
});

const computeSummaryColumnTotals = (rows = []) =>
    rows.reduce(
        (acc, row) => {
            const panels = row.panelStats || {};
            const hardware = row.hardwarePackages || {};
            const hwItems = row.hardwareItemStats || {};

            acc.deliveredPanels += Number(panels.packedPanels) || 0;
            acc.pendingPanels += Number(panels.pendingPanels) || 0;
            acc.totalPanels += Number(panels.totalPanels) || 0;
            acc.totalPackages += Number(hardware.totalPackages) || 0;
            acc.deliveredPackages += Number(hardware.deliveredPackages) || 0;
            acc.totalHardwareItems += Number(hwItems.totalHardwareItems) || 0;
            acc.pendingHardwareItems += Number(hwItems.pendingHardwareItems) || 0;
            return acc;
        },
        {
            deliveredPanels: 0,
            pendingPanels: 0,
            totalPanels: 0,
            totalPackages: 0,
            deliveredPackages: 0,
            totalHardwareItems: 0,
            pendingHardwareItems: 0,
        }
    );

const getItemDisplayName = (item, isHardware) =>
    isHardware ? item?.name || '-' : item?.name1 || item?.name || '-';

const getPackageItemNames = (pkg, isHardware) => {
    if (!pkg?.items?.length) return '-';
    const names = [...new Set(
        pkg.items.map((item) => (isHardware ? item.name : item.name1 || item.name)).filter(Boolean)
    )];
    return names.length ? names.join(', ') : '-';
};

const parseUserTaggedExpCenters = (response) => {
    const data = response?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.experienceCenters)) return data.experienceCenters;
    if (Array.isArray(data?.data?.experienceCenters)) return data.data.experienceCenters;
    if (Array.isArray(data?.data)) return data.data;
    if (data && typeof data === 'object' && (data._id || data.name || data.code)) return [data];
    return [];
};

const parseUserTaggedUsers = (response) => {
    const data = response?.data;
    if (Array.isArray(data?.user)) return data.user;
    if (Array.isArray(data?.users)) return data.users;
    if (Array.isArray(data?.userDetails)) return data.userDetails;
    if (Array.isArray(data?.data?.user)) return data.data.user;
    if (Array.isArray(data?.data?.users)) return data.data.users;
    if (Array.isArray(data?.data?.userDetails)) return data.data.userDetails;
    if (data?.user && typeof data.user === 'object') return [data.user];
    if (data?.users && typeof data.users === 'object' && !Array.isArray(data.users)) return [data.users];
    return [];
};

const getEcCode = (ec) =>
    ec?.code || ec?.expCenterCode || ec?.experienceCenterCode || '';

const resolveUserTaggedDetails = async () => {
    const response = await HttpService.getUserTaggedExpCenterDetails();
    const taggedCenters = parseUserTaggedExpCenters(response);
    const codes = [...new Set(taggedCenters.map(getEcCode).filter(Boolean))];
    const users = parseUserTaggedUsers(response);
    return { experienceCenterCodes: codes, users };
};

const buildApiParams = ({
    page,
    startDate,
    endDate,
    experienceCenterCodes,
    users,
    projectNo,
    customerName,
    dateFilterType,
    extra = {},
}) => {
    const params = { page, limit: DEFAULT_PAGE_SIZE, ...extra };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (dateFilterType) params.type = dateFilterType;
    if (experienceCenterCodes?.length) {
        params.experienceCenter = experienceCenterCodes;
    }
    if (users?.length) {
        params.user = users;
    }
    if (projectNo) {
        params.project_no = projectNo;
    }
    if (customerName) {
        params.customer_name = customerName;
    }
    return params;
};

const resolveSearchPayload = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
        return { project_no: '', customer_name: '' };
    }

    const normalized = trimmed.replace(/\s/g, '');
    const looksLikeProjectNo = /^[A-Za-z]+\d+$/i.test(normalized);

    if (looksLikeProjectNo) {
        return { project_no: trimmed, customer_name: '' };
    }

    return { project_no: '', customer_name: trimmed };
};

const buildDetailsApiParams = ({
    page,
    projectNo,
    type,
    filter,
    startDate,
    endDate,
    experienceCenterCodes,
    users,
}) => {
    const params = {
        page,
        projectNo,
        type,
        filter,
    };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (experienceCenterCodes?.length) {
        params.experienceCenter = experienceCenterCodes;
    }
    if (users?.length) {
        params.user = users;
    }
    return params;
};

const PACKAGE_DETAIL_FILTERS = ['delivered', 'pending'];
const MAX_DETAIL_PAGES = 100;

const getPanelFilterLabel = (filter) => {
    if (filter === 'pending') return 'Undelivered';
    if (filter === 'delivered') return 'Delivered';
    return 'All';
};

const getRowPanelCount = (row, filter) => {
    const panels = row?.panelStats || {};
    if (filter === 'delivered') return Number(panels.packedPanels) || 0;
    if (filter === 'pending') return Number(panels.pendingPanels) || 0;
    return Number(panels.totalPanels) || 0;
};

const getSummaryTotalForFilter = (totals, filter) => {
    if (filter === 'pending') return totals.pendingPanels;
    if (filter === 'delivered') return totals.deliveredPanels;
    return totals.totalPanels;
};

const getPackageFilterLabel = (filter) => {
    if (filter === 'pending') return 'Undelivered';
    if (filter === 'all') return 'Total Packages';
    return 'Delivered Packages';
};

const getFilterStatusLabel = (filterStatus) => {
    if (filterStatus === 'pending') return 'Undelivered';
    if (filterStatus === 'delivered') return 'Delivered';
    return filterStatus;
};

const getPackageStatusLabel = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized.includes('pending')) return 'Undelivered';
    return status || '-';
};

const getPanelStageLabel = (stage, panelOrItem) => {
    if (panelOrItem?.isDelivered) return 'Delivered';
    const normalized = String(stage || '').toLowerCase();
    if (normalized.includes('pending')) return 'Undelivered';
    return stage || '-';
};

const buildPanelExcelRow = (panel, index, { includeProject = false } = {}) => ({
    'S.No': index + 1,
    ...(includeProject ? {
        'Project No': panel.projectNo || '-',
        'Customer Name': panel.customerName || '-',
    } : {}),
    'Panel Name': panel.name1 || panel.name || '-',
    Material: panel.matname || '-',
    Barcode: panel.barcode || '-',
    'CP90 Name': panel.cp90name || '-',
    Auftrag: panel.auftrag || '-',
    Length: panel.rleng ?? '-',
    Width: panel.rwidth ?? '-',
    Thickness: panel.rthk ?? '-',
    'Space Type': panel.space_type || '-',
    Stage: getPanelStageLabel(panel.stage, panel),
});

const getPanelStageChipClass = (panelOrItem, classes) => {
    if (panelOrItem?.isDelivered) return classes.deliveredChip;
    if (String(panelOrItem?.stage || '').toLowerCase().includes('pending')) return classes.pendingChip;
    return classes.partialDeliveryChip;
};

const getHardwareDeliveredDate = (hardwarePackages = {}) =>
    hardwarePackages.deliveredDate || null;

const getPackageDeliveryDate = (pkg) => pkg?.deliveredDate || null;

const filterPackagesWithDeliveryDate = (packages, filter, packageType) => {
    if (filter !== 'delivered') return packages;
    return packages.filter((pkg) => Boolean(getPackageDeliveryDate(pkg, packageType)));
};

const getPackageStatusChipClass = (status, classes) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized.includes('pending')) return classes.pendingChip;
    return classes.deliveredChip;
};

const formatExportDate = (date) => (date ? moment(date).format('DD-MM-YYYY hh:mm A') : '');

const getPackageDedupeKey = (pkg, packageType) => {
    const packageName = (pkg?.packageName || '').trim();
    const boxId = (pkg?.boxId || '').trim();
    if (packageName) return `${packageType}|${packageName}|${boxId}`;
    if (pkg?._id) return `${packageType}|${pkg._id}`;
    return [
        packageType,
        packageName,
        boxId,
        pkg?.shippingTime || '',
        pkg?.status || '',
    ].join('|');
};

const getItemDedupeKey = (item, isHardware) => {
    if (item?._id) return String(item._id);
    if (isHardware) {
        return [item?.name, item?.quantity, item?.suppliedQuantity, item?.stage].join('|');
    }
    return [item?.barcode, item?.name1, item?.name, item?.matname, item?.auftrag].join('|');
};

const resolvePackageFilterStatus = (pkg, fallbackFilter) => {
    const status = String(pkg?.status || '').toLowerCase();
    if (status.includes('pending')) return 'pending';
    if (status.includes('delivered')) return 'delivered';
    return fallbackFilter;
};

const getDownloadTypesForTab = (tab) => {
    if (tab === PACKAGE_TYPE.MODULAR) return [PACKAGE_TYPE.MODULAR];
    if (tab === PACKAGE_TYPE.HARDWARE) return [PACKAGE_TYPE.HARDWARE];
    return [PACKAGE_TYPE.MODULAR, PACKAGE_TYPE.HARDWARE];
};

const sanitizeFileNamePart = (value, fallback) =>
    String(value || fallback)
        .trim()
        .replace(/[/\\?%*:|"<>]/g, '')
        .replace(/\s+/g, '_');

const buildRowDownloadFileName = (row) => {
    const projectNo = sanitizeFileNamePart(row.projectNo, 'project');
    const customerName = sanitizeFileNamePart(row.customerName, 'customer');
    const downloadDate = moment().format('DD-MM-YYYY');
    return `${projectNo}_${customerName}_${downloadDate}.xlsx`;
};

const omitExportColumns = (rows, columns) =>
    rows.map((rowData) => {
        const nextRow = { ...rowData };
        columns.forEach((column) => delete nextRow[column]);
        return nextRow;
    });

const ROW_DOWNLOAD_OMIT_COLUMNS = ['Project No', 'Customer Name'];
const MODAL_DOWNLOAD_OMIT_COLUMNS = ['Project No', 'Customer Name', 'Status Filter', 'Package Type'];

const getRowDownloadOmitColumns = (tab) => {
    const columns = [...ROW_DOWNLOAD_OMIT_COLUMNS, 'Stage'];
    if (tab !== PACKAGE_TYPE.BOTH) {
        columns.push('Package Type');
    }
    if (tab === PACKAGE_TYPE.HARDWARE) {
        columns.push('Box ID');
    }
    return columns;
};

const getModalDownloadOmitColumns = (type) => {
    const columns = [...MODAL_DOWNLOAD_OMIT_COLUMNS];
    if (type === PACKAGE_TYPE.HARDWARE) {
        columns.push('Box ID');
    }
    return columns;
};

const getBulkPendingPackageOmitColumns = (currentTab, packageType) => {
    const columns = ['Status Filter'];
    if (currentTab !== PACKAGE_TYPE.BOTH) {
        columns.push('Package Type');
    }
    if (packageType === PACKAGE_TYPE.HARDWARE) {
        columns.push('Box ID');
    }
    return columns;
};

const addSerialNumbersToExportRows = (rows) =>
    rows.map((row, index) => ({ 'S.No': index + 1, ...row }));

const MERGED_DETAIL_OMIT_COLUMNS = [
    'Status Filter',
    'Package Type',
    'Item S.No',
    'Total Items',
    'Delivered Qty',
    'Quantity',
];

const toModularMergedExportRow = (row) => ({
    'Project No': row['Project No'] || '',
    'Customer Name': row['Customer Name'] || '',
    'Package Name': row['Package Name'] || '',
    'Box ID': row['Box ID'] || '',
    'Package Status': row['Package Status'] || '',
    'Panel/Item Name': row['Panel/Item Name'] || row['Panel Name'] || '',
    'Material': row['Material'] || '',
    'Barcode': row['Barcode'] || '',
    'CP90 Name': row['CP90 Name'] || '',
    'Auftrag': row['Auftrag'] || '',
    'Length': row['Length'] ?? '',
    'Width': row['Width'] ?? '',
    'Thickness': row['Thickness'] ?? '',
    'Space Type': row['Space Type'] || '',
    'Stage': row['Stage'] || '',
});

const getModularMergedRowKey = (row) =>
    [
        row['Project No'],
        row['Package Name'],
        row['Panel/Item Name'],
        row['Barcode'],
        row['Material'],
    ].join('|');

const mergeModularUndeliveredExportRows = (panelApiRows, packageDetailRows, currentTab) => {
    const detailOmitColumns = currentTab === PACKAGE_TYPE.BOTH
        ? MERGED_DETAIL_OMIT_COLUMNS
        : [...MERGED_DETAIL_OMIT_COLUMNS, 'Package Type'];

    const normalizedRows = [
        ...panelApiRows.map(toModularMergedExportRow),
        ...omitExportColumns(packageDetailRows, detailOmitColumns).map(toModularMergedExportRow),
    ];

    const seen = new Set();
    const deduped = normalizedRows.filter((row) => {
        if (!row['Panel/Item Name'] && !row['Package Name']) return false;
        const key = getModularMergedRowKey(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    return addSerialNumbersToExportRows(deduped);
};

const toHardwareMergedExportRow = (row) => ({
    'Project No': row['Project No'] || '',
    'Customer Name': row['Customer Name'] || '',
    'Package Name': row['Package Name'] || '',
    'Package Status': row['Package Status'] || '',
    'Panel/Item Name': row['Panel/Item Name'] || row['Item Name'] || '',
    'Quantity': row['Quantity'] ?? '',
    'Qty Type': row['Qty Type'] || '',
    'Delivered Qty': row['Delivered Qty'] ?? '',
    'Undelivered Qty': row['Undelivered Qty'] ?? '',
    'Stage': row['Stage'] || '',
});

const getHardwareMergedRowKey = (row) =>
    [
        row['Project No'],
        row['Package Name'],
        row['Panel/Item Name'],
        row['Quantity'],
        row['Delivered Qty'],
    ].join('|');

const mergeHardwareUndeliveredExportRows = (itemApiRows, packageDetailRows, currentTab) => {
    const detailOmitColumns = currentTab === PACKAGE_TYPE.BOTH
        ? MERGED_DETAIL_OMIT_COLUMNS
        : [...MERGED_DETAIL_OMIT_COLUMNS, 'Package Type'];

    const normalizedRows = [
        ...itemApiRows.map((row) => toHardwareMergedExportRow({
            ...row,
            'Panel/Item Name': row['Item Name'] || row['Panel/Item Name'] || '',
        })),
        ...omitExportColumns(packageDetailRows, detailOmitColumns).map(toHardwareMergedExportRow),
    ];

    const seen = new Set();
    const deduped = normalizedRows.filter((row) => {
        if (!row['Panel/Item Name'] && !row['Package Name']) return false;
        const key = getHardwareMergedRowKey(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    return addSerialNumbersToExportRows(deduped);
};

const appendPendingPackageSheetsToWorkbook = (workbook, pendingModularRows, pendingHardwareRows, currentTab) => {
    if (pendingModularRows.length) {
        const exportRows = addSerialNumbersToExportRows(
            omitExportColumns(
                pendingModularRows,
                getBulkPendingPackageOmitColumns(currentTab, PACKAGE_TYPE.MODULAR)
            )
        );
        XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(exportRows),
            'Undelivered Modular Packages'
        );
    }
    if (pendingHardwareRows.length) {
        const exportRows = addSerialNumbersToExportRows(
            omitExportColumns(
                pendingHardwareRows,
                getBulkPendingPackageOmitColumns(currentTab, PACKAGE_TYPE.HARDWARE)
            )
        );
        XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(exportRows),
            'Undelivered Hardware Packages'
        );
    }
    return {
        pendingModularCount: pendingModularRows.length,
        pendingHardwareCount: pendingHardwareRows.length,
    };
};

const buildPackageSummaryRow = ({ row, packageType, filterStatus, pkg, isHardware }) => ({
    'Project No': row.projectNo || '',
    'Customer Name': row.customerName || '',
    'Package Type': packageType,
    'Status Filter': getFilterStatusLabel(filterStatus),
    'Package Name': pkg.packageName || '',
    'Box ID': isHardware ? '' : (pkg.boxId || ''),
    'Package Status': getPackageStatusLabel(pkg.status),
    'Shipping Time': formatExportDate(pkg.shippingTime),
    'Total Items': pkg.totalItems ?? (pkg.items?.length || 0),
    'Panel/Item Names': getPackageItemNames(pkg, isHardware),
});

const buildPanelDetailRows = ({ row, packageType, filterStatus, pkg, isHardware }) => {
    const baseRow = {
        'Project No': row.projectNo || '',
        'Customer Name': row.customerName || '',
        'Package Type': packageType,
        'Status Filter': getFilterStatusLabel(filterStatus),
        'Package Name': pkg.packageName || '',
        'Box ID': isHardware ? '' : (pkg.boxId || ''),
        'Package Status': getPackageStatusLabel(pkg.status),
        'Shipping Time': formatExportDate(pkg.shippingTime),
        'Total Items': pkg.totalItems ?? (pkg.items?.length || 0),
    };

    const items = Array.isArray(pkg.items) ? pkg.items : [];
    const uniqueItems = [];
    const seenItems = new Set();

    items.forEach((item) => {
        const itemKey = getItemDedupeKey(item, isHardware);
        if (seenItems.has(itemKey)) return;
        seenItems.add(itemKey);
        uniqueItems.push(item);
    });

    if (!uniqueItems.length) {
        return [{
            ...baseRow,
            'Item S.No': '',
            'Panel/Item Name': '',
            'Material': '',
            'Barcode': '',
            'CP90 Name': '',
            'Auftrag': '',
            'Length': '',
            'Width': '',
            'Thickness': '',
            'Space Type': '',
            'Quantity': '',
            'Delivered Qty': '',
            'Stage': '',
        }];
    }

    return uniqueItems.map((item, index) => ({
        ...baseRow,
        'Item S.No': index + 1,
        'Panel/Item Name': getItemDisplayName(item, isHardware),
        'Material': isHardware ? '' : (item.matname || ''),
        'Barcode': isHardware ? '' : (item.barcode || ''),
        'CP90 Name': isHardware ? '' : (item.cp90name || ''),
        'Auftrag': isHardware ? '' : (item.auftrag || ''),
        'Length': isHardware ? '' : (item.rleng ?? ''),
        'Width': isHardware ? '' : (item.rwidth ?? ''),
        'Thickness': isHardware ? '' : (item.rthk ?? ''),
        'Space Type': isHardware ? '' : (item.space_type || ''),
        'Quantity': isHardware ? (item.quantity ?? '') : '',
        'Delivered Qty': isHardware ? (item.suppliedQuantity ?? '') : '',
        'Stage': getPanelStageLabel(item.stage, item),
    }));
};

const buildBulkUndeliveredPanelExcelRows = (projectRow, panels, startIndex = 0) =>
    panels.map((panel, idx) => ({
        'S.No': startIndex + idx + 1,
        'Project No': projectRow.projectNo || '',
        'Customer Name': projectRow.customerName || '',
        'Panel/Item Name': panel.name1 || panel.name || '-',
        'Material': panel.matname || '-',
        'Barcode': panel.barcode || '-',
        'CP90 Name': panel.cp90name || '-',
        'Auftrag': panel.auftrag || '-',
        'Length': panel.rleng ?? '-',
        'Width': panel.rwidth ?? '-',
        'Thickness': panel.rthk ?? '-',
        'Space Type': panel.space_type || '-',
        'Stage': getPanelStageLabel(panel.stage, panel),
    }));

const buildBulkUndeliveredHardwareExcelRows = (projectRow, items, startIndex = 0) =>
    items.map((item, idx) => ({
        'S.No': startIndex + idx + 1,
        'Project No': projectRow.projectNo || '',
        'Customer Name': projectRow.customerName || '',
        'Item Name': item.name || '-',
        'Quantity': item.quantity ?? '-',
        'Qty Type': item.quantityType || '-',
        'Delivered Qty': item.suppliedQuantity ?? '-',
        'Undelivered Qty': item.pendingQuantity ?? '-',
        'Stage': getPanelStageLabel(item.stage, item),
    }));

const DELIVERED_DATE_TOOLTIP =
    'Delivered Date calculated as per the 90% material scanned in Delivery Stage (Tracking Tool)';

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        fontWeight: 600,
        fontSize: 12,
        padding: '9px 12px',
        lineHeight: 1.3,
        borderBottom: '1px solid #163049',
        borderRight: '1px solid rgba(255, 242, 235, 0.12)',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    },
    body: {
        fontSize: 13,
        padding: '8px 12px',
        borderBottom: '1px solid #eef2f6',
        borderRight: '1px solid #f0f3f7',
        color: '#1f2933',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    },
}))(TableCell);

const LoadingOverlay = ({ label = 'Loading...' }) => (
    <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: 180, gap: 16 }}
    >
        <CircularProgress size={40} style={{ color: '#F47E46' }} />
        <Typography variant="body2" color="textSecondary">{label}</Typography>
    </Box>
);

const DeliveredDateHeaderCell = () => (
    <StyledTableCell align="center">
        <Box display="inline-flex" alignItems="center" justifyContent="center">
            Delivered Date
            <Tooltip title={DELIVERED_DATE_TOOLTIP} arrow placement="top">
                <InfoOutlinedIcon
                    fontSize="small"
                    style={{ marginLeft: 4, fontSize: 16, cursor: 'help', color: 'inherit', opacity: 0.9 }}
                />
            </Tooltip>
        </Box>
    </StyledTableCell>
);

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(1, 2),
    },
    rootEmbedded: {
        padding: 0,
    },
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    heading: {
        fontWeight: 700,
        marginBottom: 0,
        color: '#1C375D',
    },
    filterPaper: {
        padding: theme.spacing(1.5, 0),
        marginBottom: theme.spacing(1.5),
        border: 'none',
        borderRadius: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
    },
    filterLayout: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        width: '100%',
    },
    filterRightGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        marginLeft: 'auto',
    },
    filterSearch: {
        flex: '1 1 240px',
        minWidth: 220,
        maxWidth: 300,
    },
    filterDatesGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        flexWrap: 'wrap',
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    searchField: {
        width: '100%',
    },
    dateField: {
        width: 158,
    },
    filterActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        flexShrink: 0,
        flexWrap: 'nowrap',
    },
    totalRecords: {
        fontSize: 13,
        color: '#5c6b7a',
        backgroundColor: '#fff',
        padding: theme.spacing(0.5, 1.25),
        borderRadius: 16,
        border: '1px solid #e8edf3',
    },
    table: {
        minWidth: 980,
    },
    tableSection: {
        border: '1px solid #e8edf3',
        borderRadius: theme.spacing(0.5),
        overflow: 'hidden',
        backgroundColor: '#fff',
        marginTop: theme.spacing(0.5),
    },
    tableContainer: {
        overflowX: 'auto',
    },
    tableToolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 2),
        backgroundColor: '#fafbfc',
        borderBottom: '1px solid #e8edf3',
        minHeight: 44,
    },
    tableToolbarTitle: {
        fontWeight: 600,
        color: '#1C375D',
        fontSize: 14,
    },
    tableRowEven: {
        backgroundColor: '#fafbfc',
    },
    tableRowHover: {
        '&:hover': {
            backgroundColor: '#f0f6ff',
        },
    },
    tableFooterRow: {
        backgroundColor: '#eef3f8',
        '& td': {
            fontWeight: 700,
            color: '#1C375D',
            borderTop: '2px solid #1C375D',
            borderBottom: 'none',
        },
    },
    tableFooterTotalLabel: {
        fontWeight: 700,
        color: '#1C375D',
    },
    deliveryTypeChip: {
        height: 22,
        fontSize: 11,
        fontWeight: 600,
    },
    countLink: {
        cursor: 'pointer',
        color: '#1565c0',
        fontWeight: 600,
        textDecoration: 'underline',
    },
    disabledCount: {
        color: '#9e9e9e',
        fontWeight: 600,
    },
    pendingChip: {
        backgroundColor: '#fdecea',
        color: '#c62828',
    },
    deliveredChip: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
    },
    partialDeliveryChip: {
        backgroundColor: '#fff3e0',
        color: '#ef6c00',
    },
    submitButton: {
        backgroundColor: '#F47E46',
        color: '#fff',
        minWidth: 88,
        height: 36,
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#e06d38', boxShadow: 'none' },
    },
    clearButton: {
        backgroundColor: '#fff',
        color: '#5c6b7a',
        border: '1px solid #d0d7de',
        minWidth: 88,
        height: 36,
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#f3f4f6', boxShadow: 'none' },
    },
    fetchNewButton: {
        backgroundColor: '#1C375D',
        color: '#fff',
        minWidth: 120,
        height: 36,
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#163049', boxShadow: 'none' },
    },
    disabledFetchButton: {
        backgroundColor: '#e0e0e0',
        color: '#9e9e9e',
        minWidth: 120,
        height: 36,
        boxShadow: 'none',
    },
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 12,
        marginTop: theme.spacing(2),
        minHeight: 32,
    },
    loadMoreSentinel: {
        width: '100%',
        height: 1,
    },
}));

export const PackageDeliveredPage = ({ mode = 'full', hidePageTitle = false, embedded = false, ...props }) => {
    const pageConfig = PAGE_MODE_CONFIG[mode] || PAGE_MODE_CONFIG.full;
    // Hardware section commented out — show modular panel stats only.
    const showHardwareSection = false; // mode !== 'partial';
    const classes = useStyles();
    const {
        setLoading,
        setSnackInfo,
        userDetails,
        panelDeliveryReports,
        setPanelDeliveryReports,
    } = props;

    const cachedTaggedDetailsRef = useRef(null);
    const packageDetailsCacheRef = useRef({});
    const loadMoreSentinelRef = useRef(null);
    const loadingMoreRef = useRef(false);
    const initialLoadDone = useRef(false);
    const skipFilterFetchRef = useRef(true);
    const isStoreRehydrated = useSelector((state) => state._persist?.rehydrated);
    const initialDateRange = getDefaultDateRange();
    const [modalLoading, setModalLoading] = useState(false);
    const [tab, setTab] = useState(PACKAGE_TYPE.BOTH);
    const [startDate, setStartDate] = useState(initialDateRange.startDate);
    const [endDate, setEndDate] = useState(initialDateRange.endDate);
    const [appliedStartDate, setAppliedStartDate] = useState(initialDateRange.startDate);
    const [appliedEndDate, setAppliedEndDate] = useState(initialDateRange.endDate);
    const [searchInput, setSearchInput] = useState('');
    const [appliedProjectNo, setAppliedProjectNo] = useState('');
    const [appliedCustomerName, setAppliedCustomerName] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [summary, setSummary] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [nextPage, setNextPage] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [isFetchDisabled, setIsFetchDisabled] = useState(false);
    const [fetchTimeLeft, setFetchTimeLeft] = useState(0);

    const [packageModalOpen, setPackageModalOpen] = useState(false);
    const [packageModalPage, setPackageModalPage] = useState(1);
    const [packageModalNextPage, setPackageModalNextPage] = useState(false);
    const [packageModalContext, setPackageModalContext] = useState(null);
    const [packageModalAllData, setPackageModalAllData] = useState([]);
    const [packageMeta, setPackageMeta] = useState(null);
    const [itemsPopupOpen, setItemsPopupOpen] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [downloadingProjectNo, setDownloadingProjectNo] = useState(null);
    const [downloadingAllData, setDownloadingAllData] = useState(false);

    const [panelModalOpen, setPanelModalOpen] = useState(false);
    const [panelModalLoading, setPanelModalLoading] = useState(false);
    const [panelModalContext, setPanelModalContext] = useState(null);
    const [panelModalData, setPanelModalData] = useState([]);
    const [panelModalMeta, setPanelModalMeta] = useState(null);
    const [panelModalPage, setPanelModalPage] = useState(1);
    const [panelModalNextPage, setPanelModalNextPage] = useState(false);
    const [downloadingPanelModal, setDownloadingPanelModal] = useState(false);
    const [panelDownloadOpen, setPanelDownloadOpen] = useState(false);
    const [panelDownloadProgress, setPanelDownloadProgress] = useState(0);

    const [hwItemModalOpen, setHwItemModalOpen] = useState(false);
    const [hwItemModalLoading, setHwItemModalLoading] = useState(false);
    const [hwItemModalContext, setHwItemModalContext] = useState(null);
    const [hwItemModalData, setHwItemModalData] = useState([]);
    const [hwItemModalMeta, setHwItemModalMeta] = useState(null);
    const [hwItemModalPage, setHwItemModalPage] = useState(1);
    const [hwItemModalNextPage, setHwItemModalNextPage] = useState(false);
    const [downloadingHwItemModal, setDownloadingHwItemModal] = useState(false);

    const loadTaggedDetails = useCallback(async () => {
        if (cachedTaggedDetailsRef.current) return cachedTaggedDetailsRef.current;

        try {
            const taggedDetails = await resolveUserTaggedDetails();
            cachedTaggedDetailsRef.current = taggedDetails;
            return taggedDetails;
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] getUserTaggedExpCenterDetails failed:`, error);
            const empty = { experienceCenterCodes: [], users: [] };
            cachedTaggedDetailsRef.current = empty;
            return empty;
        }
    }, [pageConfig.logLabel]);

    const fetchSummaryPage = useCallback(async ({ requestPage = 1, replace = true, updateCache = false } = {}) => {
        if (!userDetails?._id) return;

        const isAppend = !replace && requestPage > 1;

        try {
            if (isAppend) {
                if (loadingMoreRef.current) return;
                loadingMoreRef.current = true;
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const { experienceCenterCodes, users } = await loadTaggedDetails();
            const body = buildApiParams({
                page: requestPage,
                startDate: appliedStartDate,
                endDate: appliedEndDate,
                experienceCenterCodes,
                users,
                projectNo: appliedProjectNo,
                customerName: appliedCustomerName,
                dateFilterType: getDateFilterTypeForTab(tab),
            });

            const response = await pageConfig.getSummary(body);
            const result = response?.data || {};
            const pageData = Array.isArray(result.data) ? result.data : [];
            const resolvedPageSize = result.filters?.limit || DEFAULT_PAGE_SIZE;
            const resolvedTotalRecords = result.totalRecords || 0;
            const resolvedNextPage = Boolean(result.nextPage);

            setSummary((prev) => {
                const merged = isAppend ? [...prev, ...pageData] : pageData;
                if (updateCache) {
                    setPanelDeliveryReports(mode, {
                        summary: merged,
                        totalRecords: resolvedTotalRecords,
                        nextPage: resolvedNextPage,
                        page: requestPage,
                        pageSize: resolvedPageSize,
                        appliedStartDate,
                        appliedEndDate,
                        appliedProjectNo: '',
                        appliedCustomerName: '',
                    });
                }
                return merged;
            });
            setTotalRecords(resolvedTotalRecords);
            setPageSize(resolvedPageSize);
            setNextPage(resolvedNextPage);
            setPage(requestPage);
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Summary API failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to fetch delivery package summary',
                'error'
            );
        } finally {
            if (isAppend) {
                loadingMoreRef.current = false;
                setLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    }, [
        tab,
        mode,
        appliedStartDate,
        appliedEndDate,
        appliedProjectNo,
        appliedCustomerName,
        userDetails?._id,
        loadTaggedDetails,
        setLoading,
        setSnackInfo,
        setPanelDeliveryReports,
        pageConfig,
    ]);

    const loadMoreSummary = useCallback(() => {
        if (!nextPage || loadingMoreRef.current) return;
        const updateCache = !appliedProjectNo && !appliedCustomerName;
        fetchSummaryPage({ requestPage: page + 1, replace: false, updateCache });
    }, [nextPage, page, fetchSummaryPage, appliedProjectNo, appliedCustomerName]);

    useEffect(() => {
        cachedTaggedDetailsRef.current = null;
    }, [userDetails?._id]);

    useEffect(() => {
        if (!isStoreRehydrated || initialLoadDone.current) return;

        initialLoadDone.current = true;

        const cache = panelDeliveryReports?.[mode];
        const hasCachedData = cache?.isLoaded && cache.summary?.length > 0;
        const hasActiveSearch = appliedProjectNo || appliedCustomerName;

        if (hasCachedData && !hasActiveSearch) {
            setSummary(cache.summary);
            setTotalRecords(cache.totalRecords);
            setNextPage(cache.nextPage);
            setPage(cache.page);
            setPageSize(cache.pageSize || DEFAULT_PAGE_SIZE);
            if (cache.appliedStartDate) {
                setAppliedStartDate(cache.appliedStartDate);
                setStartDate(cache.appliedStartDate);
            }
            if (cache.appliedEndDate) {
                setAppliedEndDate(cache.appliedEndDate);
                setEndDate(cache.appliedEndDate);
            }
            return;
        }

        fetchSummaryPage({
            requestPage: 1,
            replace: true,
            updateCache: !hasActiveSearch,
        });
    }, [isStoreRehydrated, mode, panelDeliveryReports, fetchSummaryPage, appliedProjectNo, appliedCustomerName]);

    useEffect(() => {
        if (!isStoreRehydrated || !initialLoadDone.current) return;
        if (skipFilterFetchRef.current) {
            skipFilterFetchRef.current = false;
            return;
        }

        loadingMoreRef.current = false;
        setLoadingMore(false);
        setPage(1);
        fetchSummaryPage({
            requestPage: 1,
            replace: true,
            updateCache: !appliedProjectNo && !appliedCustomerName,
        });
    }, [
        appliedStartDate,
        appliedEndDate,
        appliedProjectNo,
        appliedCustomerName,
        tab,
        fetchSummaryPage,
        isStoreRehydrated,
    ]);

    useEffect(() => {
        const disableUntilTimestamp = localStorage.getItem(getFetchNewDataDisableUntilKey(mode));

        if (disableUntilTimestamp) {
            const now = Date.now();
            const disableUntil = parseInt(disableUntilTimestamp, 10);

            if (disableUntil > now) {
                const remainingTimeInSeconds = Math.ceil((disableUntil - now) / 1000);
                setFetchTimeLeft(remainingTimeInSeconds);
                setIsFetchDisabled(true);
            } else {
                localStorage.removeItem(getFetchNewDataDisableUntilKey(mode));
                setIsFetchDisabled(false);
                setFetchTimeLeft(0);
            }
        }

        let timer;
        if (isFetchDisabled && fetchTimeLeft > 0) {
            timer = setInterval(() => {
                setFetchTimeLeft((prevTime) => {
                    const newTime = prevTime - 1;
                    if (newTime <= 0) {
                        localStorage.removeItem(getFetchNewDataDisableUntilKey(mode));
                        setIsFetchDisabled(false);
                        return 0;
                    }
                    return newTime;
                });
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [isFetchDisabled, fetchTimeLeft, mode]);

    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        if (!sentinel) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadMoreSummary();
                }
            },
            { root: null, rootMargin: '240px', threshold: 0 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadMoreSummary, nextPage, summary.length]);

    useEffect(() => {
        packageDetailsCacheRef.current = {};
    }, [appliedStartDate, appliedEndDate, appliedProjectNo, appliedCustomerName, tab]);

    useEffect(() => {
        if (!showHardwareSection && tab === PACKAGE_TYPE.HARDWARE) {
            setTab(PACKAGE_TYPE.BOTH);
        }
    }, [showHardwareSection, tab]);

    const showDeliveryTypeColumn = mode === 'both';
    const summaryColumnTotals = useMemo(
        () => computeSummaryColumnTotals(summary),
        [summary]
    );
    const getDetailsCacheKey = (row, type, filter, modalPage) =>
        `${mode}|${row._deliverySource || mode}|${row.projectNo}|${type}|${filter}|${modalPage}|${appliedStartDate}|${appliedEndDate}|${tab}`;

    const handleSearchSubmit = () => {
        const { project_no, customer_name } = resolveSearchPayload(searchInput);
        setAppliedProjectNo(project_no);
        setAppliedCustomerName(customer_name);
    };

    const handleDateSubmit = () => {
        if (startDate && endDate && moment(endDate).isBefore(moment(startDate))) {
            setSnackInfo('End date cannot be before start date', 'warning');
            return;
        }
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
    };

    const handleClearFilter = () => {
        const defaults = getDefaultDateRange();
        setStartDate(defaults.startDate);
        setEndDate(defaults.endDate);
        setAppliedStartDate(defaults.startDate);
        setAppliedEndDate(defaults.endDate);
        setSearchInput('');
        setAppliedProjectNo('');
        setAppliedCustomerName('');
    };

    const formatFetchTimeLeft = () => {
        const minutes = Math.floor(fetchTimeLeft / 60);
        const seconds = fetchTimeLeft % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const startFetchNewDataCooldown = () => {
        const disableUntilTimestamp = Date.now() + FETCH_NEW_DATA_COOLDOWN_MS;
        localStorage.setItem(getFetchNewDataDisableUntilKey(mode), disableUntilTimestamp.toString());
        setIsFetchDisabled(true);
        setFetchTimeLeft(FETCH_NEW_DATA_COOLDOWN_SECONDS);
    };

    const handleFetchNewData = () => {
        if (isFetchDisabled) return;

        const defaults = getDefaultDateRange();
        setSearchInput('');
        setStartDate(defaults.startDate);
        setEndDate(defaults.endDate);
        setAppliedStartDate(defaults.startDate);
        setAppliedEndDate(defaults.endDate);
        setAppliedProjectNo('');
        setAppliedCustomerName('');
        startFetchNewDataCooldown();
        loadingMoreRef.current = false;
        setLoadingMore(false);
        setPage(1);
        skipFilterFetchRef.current = true;
        fetchSummaryPage({ requestPage: 1, replace: true, updateCache: true });
    };

    const applyPackageDetailsToModal = (row, type, filter, modalPage, cached) => {
        setPackageModalAllData(cached.packageData);
        setPackageMeta(cached.packageMeta);
        setPackageModalPage(modalPage);
        setPackageModalNextPage(cached.nextPage);
        setPackageModalContext({ row, type, filter });
        setPackageModalOpen(true);
    };

    const fetchPackageDetails = async (row, type, filter, modalPage = 1) => {
        const cacheKey = getDetailsCacheKey(row, type, filter, modalPage);
        const cached = packageDetailsCacheRef.current[cacheKey];

        if (cached) {
            applyPackageDetailsToModal(row, type, filter, modalPage, cached);
            return;
        }

        try {
            setModalLoading(true);
            const { experienceCenterCodes, users } = await loadTaggedDetails();
            const rowApiConfig = resolveRowApiConfig(row, mode, pageConfig);
            const body = appendDeliverySourceToBody(buildDetailsApiParams({
                page: modalPage,
                projectNo: row.projectNo,
                type,
                filter,
                startDate: appliedStartDate,
                endDate: appliedEndDate,
                experienceCenterCodes,
                users,
            }), row, mode);

            const response = await rowApiConfig.getDetails(body);
            const result = response?.data || {};

            if (result.success === false) {
                setSnackInfo(result.message || 'No package details found', 'warning');
                return;
            }

            const packageMeta = {
                type: result.type || type,
                filter: result.filter || filter,
                project: result.project || {
                    projectNo: row.projectNo,
                    customerName: row.customerName,
                    status: row.projectStatus,
                },
                totalPackages: result.totalPackages || 0,
            };
            const hasNext = Boolean(result.nextPage);
            const rawPackageData = Array.isArray(result.data) ? result.data : [];
            const packageData = filterPackagesWithDeliveryDate(rawPackageData, filter, type);
            const cacheEntry = { packageData, packageMeta, nextPage: hasNext };

            packageDetailsCacheRef.current[cacheKey] = cacheEntry;
            applyPackageDetailsToModal(row, type, filter, modalPage, cacheEntry);
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Details API failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to fetch package details',
                'error'
            );
        } finally {
            setModalLoading(false);
        }
    };

    const openPackageModal = (row, type, filter = 'delivered') => {
        fetchPackageDetails(row, type, filter, 1);
    };

    const closePackageModal = () => {
        setPackageModalOpen(false);
        setPackageModalAllData([]);
        setPackageMeta(null);
        setPackageModalPage(1);
        setPackageModalNextPage(false);
        setPackageModalContext(null);
    };

    const handlePackageModalPageChange = (newPage) => {
        if (packageModalContext && newPage >= 1) {
            fetchPackageDetails(
                packageModalContext.row,
                packageModalContext.type,
                packageModalContext.filter,
                newPage
            );
        }
    };

    const openItemsPopup = (pkg) => {
        setSelectedPackage(pkg);
        setItemsPopupOpen(true);
    };

    const closeItemsPopup = () => {
        setItemsPopupOpen(false);
        setSelectedPackage(null);
    };

    const fetchAllProjectPackages = async (row, type, filter, seenPackageKeys) => {
        const { experienceCenterCodes, users } = await loadTaggedDetails();
        const uniquePackages = [];
        let pageNum = 1;
        let hasNext = true;

        while (hasNext && pageNum <= MAX_DETAIL_PAGES) {
            const rowApiConfig = resolveRowApiConfig(row, mode, pageConfig);
            const body = appendDeliverySourceToBody(buildDetailsApiParams({
                page: pageNum,
                projectNo: row.projectNo,
                type,
                filter,
                startDate: appliedStartDate,
                endDate: appliedEndDate,
                experienceCenterCodes,
                users,
            }), row, mode);
            const response = await rowApiConfig.getDetails(body);
            const result = response?.data || {};
            if (result.success === false) break;

            const pageData = Array.isArray(result.data) ? result.data : [];
            let newPackagesOnPage = 0;

            pageData.forEach((pkg) => {
                if (!getPackageDeliveryDate(pkg, type) && filter === 'delivered') return;
                const dedupeKey = getPackageDedupeKey(pkg, type);
                if (seenPackageKeys.has(dedupeKey)) return;
                seenPackageKeys.add(dedupeKey);
                uniquePackages.push(pkg);
                newPackagesOnPage += 1;
            });

            hasNext = Boolean(result.nextPage);
            pageNum += 1;

            if (!pageData.length) break;
            if (!newPackagesOnPage && pageNum > 2) break;
        }

        return uniquePackages;
    };

    const packagesToExportData = (row, type, filter, packages) => {
        const isHardware = type === PACKAGE_TYPE.HARDWARE;
        const packageRows = [];
        const panelRows = [];

        packages.forEach((pkg) => {
            const filterStatus = resolvePackageFilterStatus(pkg, filter);
            packageRows.push(
                buildPackageSummaryRow({
                    row,
                    packageType: type,
                    filterStatus,
                    pkg,
                    isHardware,
                })
            );
            panelRows.push(
                ...buildPanelDetailRows({
                    row,
                    packageType: type,
                    filterStatus,
                    pkg,
                    isHardware,
                })
            );
        });

        return { packageRows, panelRows };
    };

    const exportPackagesToExcel = (row, packageRows, panelRows, fileSuffix, options = {}) => {
        const { omitColumns = [], useRowDownloadFileName = false } = options;
        const exportPackageRows = omitColumns.length
            ? omitExportColumns(packageRows, omitColumns)
            : packageRows;
        const exportPanelRows = omitColumns.length
            ? omitExportColumns(panelRows, omitColumns)
            : panelRows;

        const workbook = XLSX.utils.book_new();
        const packagesSheet = XLSX.utils.json_to_sheet(exportPackageRows);
        XLSX.utils.book_append_sheet(workbook, packagesSheet, 'Packages');

        if (exportPanelRows.length) {
            const panelSheet = XLSX.utils.json_to_sheet(exportPanelRows);
            XLSX.utils.book_append_sheet(workbook, panelSheet, 'Panel Details');
        }

        const fileName = useRowDownloadFileName
            ? buildRowDownloadFileName(row)
            : `${row.projectNo}_${fileSuffix}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };

    const buildSummaryExcelRows = (rows, currentTab) => {
        const formatD = (date) => (date ? moment(date).format('DD-MM-YYYY hh:mm A') : '-');
        return rows.map((row, index) => {
            const m = row.productPackages || {};
            const h = row.hardwarePackages || {};
            const p = row.panelStats || {};
            const hw = row.hardwareItemStats || {};

            const base = {
                'S.No': index + 1,
                'Project No': row.projectNo || '',
                'Customer Name': row.customerName || '',
                'Project Status': row.projectStatus || '',
            };
            if (currentTab === PACKAGE_TYPE.MODULAR || currentTab === PACKAGE_TYPE.BOTH) {
                base['Modular - Delivered Panel'] = p.packedPanels ?? 0;
                base['Modular - Pending Panels'] = p.pendingPanels ?? 0;
                base['Modular - Total Panels'] = p.totalPanels ?? 0;
            }
            if (currentTab === PACKAGE_TYPE.HARDWARE || currentTab === PACKAGE_TYPE.BOTH) {
                base['Hardware - Total Packages'] = h.totalPackages ?? 0;
                base['Hardware - Delivered Packages'] = h.deliveredPackages ?? 0;
                base['Hardware - Undelivered'] = h.pendingPackages ?? 0;
                base['Hardware Total line-item'] = hw.totalHardwareItems ?? 0;
                base['Hardware UnDelivered Items (QTY)'] = hw.pendingHardwareItems ?? 0;
            }
            base['Modular Delivery Date'] = formatD(row.deliveryDate || m.deliveredDate);
            base['Hardware Delivery Date'] = formatD(getHardwareDeliveredDate(h));
            return base;
        });
    };

    const fetchAllSummaryRows = async () => {
        const { experienceCenterCodes, users } = await loadTaggedDetails();
        const allRows = [];
        let pageNum = 1;
        let hasNext = true;
        const MAX_PAGES = 200;

        while (hasNext && pageNum <= MAX_PAGES) {
            const body = buildApiParams({
                page: pageNum,
                startDate: appliedStartDate,
                endDate: appliedEndDate,
                experienceCenterCodes,
                users,
                projectNo: appliedProjectNo,
                customerName: appliedCustomerName,
                dateFilterType: getDateFilterTypeForTab(tab),
            });
            const response = await pageConfig.getSummary(body);
            const result = response?.data || {};
            const pageData = Array.isArray(result.data) ? result.data : [];
            allRows.push(...pageData);
            hasNext = Boolean(result.nextPage);
            pageNum += 1;
            if (!pageData.length) break;
        }
        return allRows;
    };

    const fetchAllProjectPanelDetails = async (row, filter) => {
        const { experienceCenterCodes, users } = await loadTaggedDetails();
        const allPanels = [];
        let pageNum = 1;
        let hasNext = true;

        while (hasNext && pageNum <= MAX_DETAIL_PAGES) {
            const rowApiConfig = resolveRowApiConfig(row, mode, pageConfig);
            const body = appendDeliverySourceToBody({
                page: pageNum,
                projectNo: row.projectNo,
                filter,
            }, row, mode);
            if (appliedStartDate) body.startDate = appliedStartDate;
            if (appliedEndDate) body.endDate = appliedEndDate;
            if (experienceCenterCodes?.length) body.experienceCenter = experienceCenterCodes;
            if (users?.length) body.user = users;

            const response = await rowApiConfig.getPanelDetails(body);
            const result = response?.data || {};
            if (result.success === false) break;

            const pageData = Array.isArray(result.data) ? result.data : [];
            allPanels.push(...pageData);
            hasNext = Boolean(result.nextPage);
            pageNum += 1;
            if (!pageData.length) break;
        }

        return allPanels;
    };

    const fetchAllProjectHardwareItems = async (row, filter) => {
        const { experienceCenterCodes, users } = await loadTaggedDetails();
        const allItems = [];
        let pageNum = 1;
        let hasNext = true;

        while (hasNext && pageNum <= MAX_DETAIL_PAGES) {
            const rowApiConfig = resolveRowApiConfig(row, mode, pageConfig);
            const body = appendDeliverySourceToBody({
                page: pageNum,
                projectNo: row.projectNo,
                filter,
            }, row, mode);
            if (appliedStartDate) body.startDate = appliedStartDate;
            if (appliedEndDate) body.endDate = appliedEndDate;
            if (experienceCenterCodes?.length) body.experienceCenter = experienceCenterCodes;
            if (users?.length) body.user = users;

            const response = await rowApiConfig.getHardwareItemDetails(body);
            const result = response?.data || {};
            if (result.success === false) break;

            const pageData = Array.isArray(result.data) ? result.data : [];
            allItems.push(...pageData);
            hasNext = Boolean(result.nextPage);
            pageNum += 1;
            if (!pageData.length) break;
        }

        return allItems;
    };

    const buildPanelDetailsRequestBody = async (row, filter, modalPage = 1) => {
        const { experienceCenterCodes, users } = await loadTaggedDetails();
        const body = appendDeliverySourceToBody({
            page: modalPage,
            projectNo: row.projectNo,
            filter,
        }, row, mode);
        if (appliedStartDate) body.startDate = appliedStartDate;
        if (appliedEndDate) body.endDate = appliedEndDate;
        if (experienceCenterCodes?.length) body.experienceCenter = experienceCenterCodes;
        if (users?.length) body.user = users;
        return body;
    };

    const fetchProjectPanelDetailsPage = async (row, filter, modalPage = 1) => {
        const rowApiConfig = resolveRowApiConfig(row, mode, pageConfig);
        const body = await buildPanelDetailsRequestBody(row, filter, modalPage);
        const response = await rowApiConfig.getPanelDetails(body);
        const result = response?.data || {};
        if (result.success === false) {
            return { panels: [], nextPage: false, totalPanels: 0 };
        }
        return {
            panels: Array.isArray(result.data) ? result.data : [],
            nextPage: Boolean(result.nextPage),
            totalPanels: result.totalPanels ?? result.total ?? 0,
        };
    };

    const getBulkPanelDetailsApis = () => {
        if (mode === 'partial') {
            return [(body) => HttpService.getDeliveryPackagePartialPanelDetails(body)];
        }
        if (mode === 'full') {
            return [(body) => HttpService.getDeliveryPackagePanelDetails(body)];
        }
        return [
            (body) => HttpService.getDeliveryPackagePartialPanelDetails(body),
            (body) => HttpService.getDeliveryPackagePanelDetails(body),
        ];
    };

    const buildBulkPanelDetailsBody = async (page, limit, filter) => {
        const { experienceCenterCodes, users } = await loadTaggedDetails();
        const body = {
            page,
            limit: Math.min(Math.max(limit, 1), 500),
            filter,
        };
        if (appliedStartDate) body.startDate = appliedStartDate;
        if (appliedEndDate) body.endDate = appliedEndDate;
        if (experienceCenterCodes?.length) body.experienceCenter = experienceCenterCodes;
        if (users?.length) body.user = users;
        return body;
    };

    const fetchBulkPanelDetailsPageFromApi = async (getPanelDetails, page, limit, filter) => {
        const body = await buildBulkPanelDetailsBody(page, limit, filter);
        const response = await getPanelDetails(body);
        const result = response?.data || {};
        if (result.success === false) {
            return { panels: [], nextPage: false, totalPanels: 0 };
        }
        return {
            panels: Array.isArray(result.data) ? result.data : [],
            nextPage: Boolean(result.nextPage),
            totalPanels: result.totalPanels ?? result.total ?? 0,
        };
    };

    const fetchBulkPanelPreview = async (filter) => {
        const apis = getBulkPanelDetailsApis();
        const previewPanels = [];

        for (const getPanelDetails of apis) {
            if (previewPanels.length >= PANEL_PREVIEW_LIMIT) break;
            const remaining = PANEL_PREVIEW_LIMIT - previewPanels.length;
            const { panels } = await fetchBulkPanelDetailsPageFromApi(
                getPanelDetails,
                1,
                remaining,
                filter
            );
            previewPanels.push(...panels);
        }

        const totalCount = getSummaryTotalForFilter(summaryColumnTotals, filter);

        return {
            panels: previewPanels.slice(0, PANEL_PREVIEW_LIMIT),
            totalCount,
            previewCount: previewPanels.length,
            isPreview: totalCount > previewPanels.length,
        };
    };

    const buildPanelDownloadPayload = async (row, filter) => {
        const { experienceCenterCodes, users } = await loadTaggedDetails();
        let deliverySource = mode;
        if (mode === 'both' && row?._deliverySource) {
            deliverySource = row._deliverySource;
        }

        const payload = {
            filter,
            deliverySource,
            startDate: appliedStartDate,
            endDate: appliedEndDate,
        };

        if (row?.projectNo) payload.projectNo = row.projectNo;
        if (experienceCenterCodes?.length) payload.experienceCenter = experienceCenterCodes;
        if (users?.length) payload.user = users;

        return payload;
    };

    const downloadPanelDetailsViaServer = async ({ row, filter, totalCount, fileName }) => {
        setPanelDownloadProgress(0);
        setPanelDownloadOpen(true);

        try {
            const payload = await buildPanelDownloadPayload(row, filter);
            payload.fileName = fileName;

            const res = await downloadDeliveryPackagePanelCsv({
                payload,
                totalCount,
                onProgress: setPanelDownloadProgress,
            });

            saveCsvBlob(res.data, fileName);
            setSnackInfo(`Downloaded ${Number(totalCount || 0).toLocaleString()} panel records`, 'success');
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Panel CSV download failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to download panel details',
                'error'
            );
        } finally {
            setPanelDownloadOpen(false);
            setPanelDownloadProgress(0);
        }
    };

    const buildPanelFocusedExcelRows = (rows, currentTab) => rows.map((row, index) => {
            const base = {
                'S.No': index + 1,
                'Project No': row.projectNo || '',
                'Customer Name': row.customerName || '',
                'Project Status': row.projectStatus || '',
            };
            if (currentTab === PACKAGE_TYPE.MODULAR || currentTab === PACKAGE_TYPE.BOTH) {
                const p = row.panelStats || {};
                base['Total Panels'] = p.totalPanels ?? 0;
                base['Delivered Panels'] = p.packedPanels ?? 0;
                base['Undelivered Panels'] = p.pendingPanels ?? 0;
            }
            if (currentTab === PACKAGE_TYPE.HARDWARE || currentTab === PACKAGE_TYPE.BOTH) {
                const hw = row.hardwareItemStats || {};
                base['Total Hardware Items'] = hw.totalHardwareItems ?? 0;
                base['Delivered Hardware Items'] = hw.suppliedHardwareItems ?? 0;
                base['Undelivered Hardware Items'] = hw.pendingHardwareItems ?? 0;
            }
            return base;
        });

    const fetchBulkPendingPackageRows = async (rows, currentTab) => {
        const pendingModularRows = [];
        const pendingHardwareRows = [];
        const pendingModularDetailRows = [];
        const pendingHardwareDetailRows = [];

        if (currentTab === PACKAGE_TYPE.MODULAR || currentTab === PACKAGE_TYPE.BOTH) {
            setSnackInfo('Fetching undelivered modular packages for all projects...', 'info');
            for (const row of rows) {
                if ((row.productPackages?.pendingPackages ?? 0) <= 0) continue;
                const seenPackageKeys = new Set();
                const packages = await fetchAllProjectPackages(
                    row,
                    PACKAGE_TYPE.MODULAR,
                    'pending',
                    seenPackageKeys
                );
                const exportData = packagesToExportData(
                    row,
                    PACKAGE_TYPE.MODULAR,
                    'pending',
                    packages
                );
                pendingModularRows.push(...exportData.packageRows);
                pendingModularDetailRows.push(...exportData.panelRows);
            }
        }

        if (currentTab === PACKAGE_TYPE.HARDWARE || currentTab === PACKAGE_TYPE.BOTH) {
            setSnackInfo('Fetching undelivered hardware packages for all projects...', 'info');
            for (const row of rows) {
                if ((row.hardwarePackages?.pendingPackages ?? 0) <= 0) continue;
                const seenPackageKeys = new Set();
                const packages = await fetchAllProjectPackages(
                    row,
                    PACKAGE_TYPE.HARDWARE,
                    'pending',
                    seenPackageKeys
                );
                const exportData = packagesToExportData(
                    row,
                    PACKAGE_TYPE.HARDWARE,
                    'pending',
                    packages
                );
                pendingHardwareRows.push(...exportData.packageRows);
                pendingHardwareDetailRows.push(...exportData.panelRows);
            }
        }

        return {
            pendingModularRows,
            pendingHardwareRows,
            pendingModularDetailRows,
            pendingHardwareDetailRows,
        };
    };

    const buildRenderedTableExcelRows = (rows) => {
        const formatD = (date) => (date ? moment(date).format('DD-MM-YYYY hh:mm A') : '-');
        return rows.map((row, index) => {
            const panels = row.panelStats || {};
            const product = row.productPackages || {};
            const base = {
                'S.No': index + 1,
                'Project No': row.projectNo || '',
                'Customer Name': row.customerName || '',
            };
            if (showDeliveryTypeColumn) {
                base['Delivery Type'] = getDeliveryTypeLabel(row);
            }
            return {
                ...base,
                'Delivered Panel': panels.packedPanels ?? 0,
                'Pending Panels': panels.pendingPanels ?? 0,
                'Total Panels': panels.totalPanels ?? 0,
                'Delivered Date': formatD(row.deliveryDate || product.deliveredDate),
            };
        });
    };

    const handleDownloadAllTableData = async () => {
        try {
            setDownloadingAllData(true);
            setLoading(true);
            setSnackInfo('Fetching all project records for download...', 'info');

            const allRows = await fetchAllSummaryRows();

            if (!allRows.length) {
                setSnackInfo('No records found to download', 'warning');
                return;
            }

            const prefix = mode === 'partial'
                ? 'Partial_Delivery'
                : mode === 'full'
                    ? 'Full_Package_Delivered'
                    : 'Panel_Delivery_Both';
            const downloadDate = moment().format('DD-MM-YYYY');
            const excelRows = buildRenderedTableExcelRows(allRows);
            const workbook = XLSX.utils.book_new();
            const sheet = XLSX.utils.json_to_sheet(excelRows);
            XLSX.utils.book_append_sheet(workbook, sheet, 'Table Data');
            XLSX.writeFile(workbook, `${prefix}_TableData_${downloadDate}.xlsx`);
            setSnackInfo(`Downloaded ${excelRows.length} record(s)`, 'success');
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Download failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to download data',
                'error'
            );
        } finally {
            setDownloadingAllData(false);
            setLoading(false);
        }
    };

    const handleDownloadRowPackages = async (row) => {
        try {
            setDownloadingProjectNo(`row-${row.projectNo}`);
            setLoading(true);
            setSnackInfo('Fetching all project packages for download...', 'info');

            const types = getDownloadTypesForTab(tab);
            const packageRows = [];
            const panelRows = [];
            const seenPackageKeys = new Set();

            for (const type of types) {
                for (const filter of PACKAGE_DETAIL_FILTERS) {
                    const packages = await fetchAllProjectPackages(row, type, filter, seenPackageKeys);
                    const exportData = packagesToExportData(row, type, filter, packages);
                    packageRows.push(...exportData.packageRows);
                    panelRows.push(...exportData.panelRows);
                }
            }

            if (!packageRows.length) {
                setSnackInfo('No package details found to download', 'warning');
                return;
            }

            const fileSuffix = mode === 'partial'
                ? 'partial_all_packages'
                : mode === 'full'
                    ? 'full_all_packages'
                    : 'both_all_packages';
            exportPackagesToExcel(row, packageRows, panelRows, fileSuffix, {
                omitColumns: getRowDownloadOmitColumns(tab),
                useRowDownloadFileName: true,
            });
            setSnackInfo(`Downloaded ${packageRows.length} package(s)`, 'success');
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Row download failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to download package details',
                'error'
            );
        } finally {
            setDownloadingProjectNo(null);
            setLoading(false);
        }
    };

    const handleDownloadModalPackages = async () => {
        if (!packageModalContext) return;

        const { row, type, filter } = packageModalContext;

        try {
            setDownloadingProjectNo(`modal-${row.projectNo}`);
            setLoading(true);
            setSnackInfo(`Fetching ${getPackageFilterLabel(filter).toLowerCase()} ${type} packages for download...`, 'info');

            const seenPackageKeys = new Set();
            const packages = await fetchAllProjectPackages(row, type, filter, seenPackageKeys);
            const { packageRows, panelRows } = packagesToExportData(row, type, filter, packages);

            if (!packageRows.length) {
                setSnackInfo('No package details found to download', 'warning');
                return;
            }

            const filterLabel = String(filter || 'packages').toLowerCase();
            const filePrefix = mode === 'partial' ? 'partial' : mode === 'full' ? 'full' : 'both';
            exportPackagesToExcel(row, packageRows, panelRows, `${filePrefix}_${type}_${filterLabel}`, {
                omitColumns: getModalDownloadOmitColumns(type),
            });
            setSnackInfo(
                `Downloaded ${packageRows.length} ${getPackageFilterLabel(filter).toLowerCase()} ${type} package(s)`,
                'success'
            );
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Modal download failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to download package details',
                'error'
            );
        } finally {
            setDownloadingProjectNo(null);
            setLoading(false);
        }
    };

    const fetchPanelDetails = async (row, filter, modalPage = 1) => {
        try {
            setPanelModalLoading(true);
            const { panels, nextPage, totalPanels } = await fetchProjectPanelDetailsPage(row, filter, modalPage);

            if (!panels.length && modalPage === 1) {
                setSnackInfo('No panel details found', 'warning');
                return;
            }

            const meta = {
                filter,
                totalPanels: totalPanels || panels.length,
                count: totalPanels || panels.length,
                project: {
                    projectNo: row.projectNo,
                    customerName: row.customerName,
                },
                isPreview: false,
            };

            setPanelModalData(panels);
            setPanelModalMeta(meta);
            setPanelModalPage(modalPage);
            setPanelModalNextPage(nextPage);
            setPanelModalContext({ row, filter, isAggregate: false });
            setPanelModalOpen(true);
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Panel details API failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to fetch panel details',
                'error'
            );
        } finally {
            setPanelModalLoading(false);
        }
    };

    const openPanelModal = (row, filter) => fetchPanelDetails(row, filter, 1);

    const openTotalPanelModal = async (filter) => {
        const totalCount = getSummaryTotalForFilter(summaryColumnTotals, filter);

        if (!totalCount || !isPanelFilterClickable(filter)) return;

        setPanelModalOpen(true);
        setPanelModalLoading(true);
        setPanelModalData([]);
        setPanelModalMeta(null);
        setPanelModalContext(null);

        try {
            const result = await fetchBulkPanelPreview(filter);

            if (!result.panels.length) {
                setSnackInfo(`No ${getPanelFilterLabel(filter).toLowerCase()} panel details found`, 'warning');
                setPanelModalOpen(false);
                return;
            }

            setPanelModalData(result.panels);
            setPanelModalMeta({
                filter,
                totalPanels: result.totalCount,
                count: result.totalCount,
                previewCount: result.previewCount,
                isPreview: result.isPreview,
                project: {
                    projectNo: 'All Projects',
                    customerName: getPanelFilterLabel(filter),
                },
            });
            setPanelModalPage(1);
            setPanelModalNextPage(false);
            setPanelModalContext({
                filter,
                isAggregate: true,
                isBulkPanel: true,
            });
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Bulk panel details failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to fetch panel details',
                'error'
            );
            setPanelModalOpen(false);
        } finally {
            setPanelModalLoading(false);
        }
    };

    const closePanelModal = () => {
        setPanelModalOpen(false);
        setPanelModalData([]);
        setPanelModalMeta(null);
        setPanelModalPage(1);
        setPanelModalNextPage(false);
        setPanelModalContext(null);
    };

    const handlePanelModalPageChange = (newPage) => {
        if (panelModalContext?.isAggregate) return;
        if (panelModalContext?.row && newPage >= 1) {
            fetchPanelDetails(panelModalContext.row, panelModalContext.filter, newPage);
        }
    };

    const handleDownloadPanelModalData = async () => {
        if (!panelModalContext) return;
        const { row, filter, isAggregate, isBulkPanel } = panelModalContext;
        const totalCount = panelModalMeta?.count || panelModalMeta?.totalPanels || 0;

        if (!totalCount) {
            setSnackInfo('No panel data to download', 'warning');
            return;
        }

        const filterLabel = getPanelFilterLabel(filter);
        const downloadDate = moment().format('DD-MM-YYYY');

        if (isAggregate && isBulkPanel) {
            const filePrefix = mode === 'partial' ? 'Partial' : mode === 'full' ? 'Full' : 'Both';
            await downloadPanelDetailsViaServer({
                row: null,
                filter,
                totalCount,
                fileName: `${filePrefix}_${filterLabel}_AllProjects_Panels_${downloadDate}.csv`,
            });
            return;
        }

        const projectNo = sanitizeFileNamePart(row.projectNo, 'project');
        const customerName = sanitizeFileNamePart(row.customerName, 'customer');
        await downloadPanelDetailsViaServer({
            row,
            filter,
            totalCount,
            fileName: `${projectNo}_${customerName}_${filterLabel}Panels_${downloadDate}.csv`,
        });
    };

    const fetchHardwareItemDetails = async (row, filter, modalPage = 1) => {
        try {
            setHwItemModalLoading(true);
            const { experienceCenterCodes, users } = await loadTaggedDetails();
            const rowApiConfig = resolveRowApiConfig(row, mode, pageConfig);
            const body = appendDeliverySourceToBody({
                page: modalPage,
                projectNo: row.projectNo,
                filter,
            }, row, mode);
            if (appliedStartDate) body.startDate = appliedStartDate;
            if (appliedEndDate) body.endDate = appliedEndDate;
            if (experienceCenterCodes?.length) body.experienceCenter = experienceCenterCodes;
            if (users?.length) body.user = users;

            const response = await rowApiConfig.getHardwareItemDetails(body);
            const result = response?.data || {};

            if (result.success === false) {
                setSnackInfo(result.message || 'No hardware item details found', 'warning');
                return;
            }

            const items = Array.isArray(result.data) ? result.data : [];
            const meta = {
                filter,
                totalItems: result.totalItems ?? result.total ?? items.length,
                project: { projectNo: row.projectNo, customerName: row.customerName },
            };

            setHwItemModalData(items);
            setHwItemModalMeta(meta);
            setHwItemModalPage(modalPage);
            setHwItemModalNextPage(Boolean(result.nextPage));
            setHwItemModalContext({ row, filter });
            setHwItemModalOpen(true);
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] Hardware item details API failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to fetch hardware item details',
                'error'
            );
        } finally {
            setHwItemModalLoading(false);
        }
    };

    const openHwItemModal = (row, filter) => fetchHardwareItemDetails(row, filter, 1);

    const closeHwItemModal = () => {
        setHwItemModalOpen(false);
        setHwItemModalData([]);
        setHwItemModalMeta(null);
        setHwItemModalPage(1);
        setHwItemModalNextPage(false);
        setHwItemModalContext(null);
    };

    const handleHwItemModalPageChange = (newPage) => {
        if (hwItemModalContext && newPage >= 1) {
            fetchHardwareItemDetails(hwItemModalContext.row, hwItemModalContext.filter, newPage);
        }
    };

    const handleDownloadHwItemModalData = async () => {
        if (!hwItemModalContext) return;
        const { row, filter } = hwItemModalContext;
        try {
            setDownloadingHwItemModal(true);
            setLoading(true);
            setSnackInfo('Fetching all hardware item records for download...', 'info');

            const allItems = await fetchAllProjectHardwareItems(row, filter);

            if (!allItems.length) {
                setSnackInfo('No hardware item details found to download', 'warning');
                return;
            }

            const filterLabel = filter === 'pending' ? 'Undelivered' : 'All';
            const excelRows = allItems.map((item, idx) => ({
                'S.No': idx + 1,
                'Item Name': item.name || '-',

                'Quantity': item.quantity ?? '-',
                'Qty Type': item.quantityType || '-',
                'Delivered Qty': item.suppliedQuantity ?? '-',
                'Undelivered Qty': item.pendingQuantity ?? '-',
                'Stage': getPanelStageLabel(item.stage, item),
                'Remarks': item.remarks || '-',
            }));

            const workbook = XLSX.utils.book_new();
            const sheet = XLSX.utils.json_to_sheet(excelRows);
            XLSX.utils.book_append_sheet(workbook, sheet, `${filterLabel} Hardware Items`);

            const projectNo = sanitizeFileNamePart(row.projectNo, 'project');
            const customerName = sanitizeFileNamePart(row.customerName, 'customer');
            const downloadDate = moment().format('DD-MM-YYYY');
            XLSX.writeFile(workbook, `${projectNo}_${customerName}_${filterLabel}HwItems_${downloadDate}.xlsx`);
            setSnackInfo(`Downloaded ${allItems.length} hardware item(s)`, 'success');
        } catch (error) {
            console.error(`[${pageConfig.logLabel}] HW item modal download failed:`, error);
            setSnackInfo(
                error?.response?.data?.message || error?.message || 'Failed to download hardware item details',
                'error'
            );
        } finally {
            setDownloadingHwItemModal(false);
            setLoading(false);
        }
    };

    const isRowDownloadLoading = (projectNo) => downloadingProjectNo === `row-${projectNo}`;
    const isModalDownloadLoading = (projectNo) => downloadingProjectNo === `modal-${projectNo}`;

    const renderPackageCount = (count, row, type, filter = 'delivered') => {
        const value = count ?? 0;
        if (!value) {
            return <span className={classes.disabledCount}>{value}</span>;
        }
        const tooltipText = filter === 'all'
            ? 'View all packages'
            : filter === 'pending'
                ? 'View undelivered packages'
                : 'View delivered packages';
        return (
            <Tooltip title={tooltipText}>
                <span
                    className={classes.countLink}
                    onClick={() => openPackageModal(row, type, filter)}
                >
                    {value}
                </span>
            </Tooltip>
        );
    };

    const isPanelFilterClickable = (filter) => {
        if (mode === 'partial') {
            return filter === 'pending' || filter === 'delivered' || filter === 'all';
        }
        if (mode === 'full' || mode === 'both') {
            return filter === 'delivered' || filter === 'all';
        }
        return false;
    };

    const getPanelCountTooltip = (filter, isTotal = false) => {
        if (filter === 'pending') {
            return isTotal ? 'View all undelivered panels' : 'View undelivered panels';
        }
        if (filter === 'delivered') {
            return isTotal ? 'View all delivered panels' : 'View delivered panels';
        }
        return isTotal ? 'View all panels' : 'View all panels';
    };

    const renderPanelCount = (count, row, filter) => {
        const value = count ?? 0;
        if (!value || !isPanelFilterClickable(filter)) {
            return <span className={classes.disabledCount}>{value}</span>;
        }
        return (
            <Tooltip title={getPanelCountTooltip(filter)}>
                <span className={classes.countLink} onClick={() => openPanelModal(row, filter)}>
                    {value}
                </span>
            </Tooltip>
        );
    };

    const renderTotalPanelCount = (count, filter) => {
        const value = count ?? 0;
        if (!value || !isPanelFilterClickable(filter)) {
            return <span className={value ? undefined : classes.disabledCount}>{value}</span>;
        }
        return (
            <Tooltip title={getPanelCountTooltip(filter, true)}>
                <span
                    className={classes.countLink}
                    onClick={() => openTotalPanelModal(filter)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') openTotalPanelModal(filter);
                    }}
                >
                    {value}
                </span>
            </Tooltip>
        );
    };

    const renderHardwareItemCount = (count, row, filter) => {
        const value = count ?? 0;
        if (!value) {
            return <span className={classes.disabledCount}>{value}</span>;
        }
        const tooltipText = filter === 'pending' ? 'View undelivered hardware items' : 'View all hardware items';
        return (
            <Tooltip title={tooltipText}>
                <span className={classes.countLink} onClick={() => openHwItemModal(row, filter)}>
                    {value}
                </span>
            </Tooltip>
        );
    };

    const formatDate = (date) => (date ? moment(date).format('DD-MM-YYYY hh:mm A') : '-');

    const packageModalTotalPages = Math.max(
        1,
        Math.ceil((packageMeta?.totalPackages || 0) / DEFAULT_PAGE_SIZE)
    );
    const visibleModalPackages = filterPackagesWithDeliveryDate(
        packageModalAllData,
        packageModalContext?.filter,
        packageMeta?.type
    );
    const isHardwareModal = packageMeta?.type === PACKAGE_TYPE.HARDWARE;

    const getSerialNumber = (index, currentPage, size = pageSize) =>
        (currentPage - 1) * size + index + 1;

    const modularColumnCount = 4;
    const bothTypeColSpan = showHardwareSection ? 12 : (showDeliveryTypeColumn ? 8 : 7);
    const singleTypeColSpan = (type) => (type === PACKAGE_TYPE.MODULAR ? 7 : 8);

    const getPartialDeliveryDate = (row) => row.deliveryDate || row.productPackages?.deliveredDate || null;

    const renderPartialPanelStatsHeader = () => (
        <>
            <StyledTableCell align="center">Delivered Panel</StyledTableCell>
            <StyledTableCell align="center">Pending Panels</StyledTableCell>
            <StyledTableCell align="center">Total Panels</StyledTableCell>
            <DeliveredDateHeaderCell />
        </>
    );

    const renderPartialPanelStatsCells = (row, panels) => (
        <>
            <StyledTableCell align="center">{renderPanelCount(panels.packedPanels, row, 'delivered')}</StyledTableCell>
            <StyledTableCell align="center">{renderPanelCount(panels.pendingPanels, row, 'pending')}</StyledTableCell>
            <StyledTableCell align="center">{renderPanelCount(panels.totalPanels, row, 'all')}</StyledTableCell>
            <StyledTableCell align="center">{formatDate(getPartialDeliveryDate(row))}</StyledTableCell>
        </>
    );

    const renderSummaryTotalsRow = () => {
        const totalsLabel = nextPage ? 'Total (loaded)' : 'Total';

        return (
            <TableRow className={classes.tableFooterRow}>
                <StyledTableCell align="center" />
                <StyledTableCell align="left" />
                <StyledTableCell align="left" className={classes.tableFooterTotalLabel}>
                    {totalsLabel}
                </StyledTableCell>
                {showDeliveryTypeColumn && <StyledTableCell align="center" />}
                <StyledTableCell align="center">
                    {renderTotalPanelCount(summaryColumnTotals.deliveredPanels, 'delivered')}
                </StyledTableCell>
                <StyledTableCell align="center">
                    {renderTotalPanelCount(summaryColumnTotals.pendingPanels, 'pending')}
                </StyledTableCell>
                <StyledTableCell align="center">
                    {renderTotalPanelCount(summaryColumnTotals.totalPanels, 'all')}
                </StyledTableCell>
                <StyledTableCell align="center">-</StyledTableCell>
                {showHardwareSection && (
                    <>
                        <StyledTableCell align="center">{summaryColumnTotals.totalPackages}</StyledTableCell>
                        <StyledTableCell align="center">{summaryColumnTotals.deliveredPackages}</StyledTableCell>
                        <StyledTableCell align="center">{summaryColumnTotals.totalHardwareItems}</StyledTableCell>
                        <StyledTableCell align="center">{summaryColumnTotals.pendingHardwareItems}</StyledTableCell>
                        <StyledTableCell align="center">-</StyledTableCell>
                    </>
                )}
            </TableRow>
        );
    };

    const renderModularHeader = (label, type) => (
        <TableRow>
            <StyledTableCell>S.No</StyledTableCell>
            <StyledTableCell>Project No</StyledTableCell>
            <StyledTableCell>Customer Name</StyledTableCell>
            {type === PACKAGE_TYPE.MODULAR ? (
                renderPartialPanelStatsHeader()
            ) : (
                <>
                    <StyledTableCell align="center">Total Packages</StyledTableCell>
                    <StyledTableCell align="center">Delivered Packages</StyledTableCell>
                    <StyledTableCell align="center">Total Items</StyledTableCell>
                    <StyledTableCell align="center">Undelivered Items</StyledTableCell>
                    <DeliveredDateHeaderCell />
                </>
            )}
        </TableRow>
    );

    const renderSingleTypeRows = (type) => {
        const pkgKey = type === PACKAGE_TYPE.MODULAR ? 'productPackages' : 'hardwarePackages';
        const label = type === PACKAGE_TYPE.MODULAR ? 'Modular' : 'Hardware';
        const isModular = type === PACKAGE_TYPE.MODULAR;
        const isHardware = type === PACKAGE_TYPE.HARDWARE;
        if (!summary.length) {
            return (
                <TableRow>
                    <TableCell colSpan={singleTypeColSpan(type)} align="center">No records found</TableCell>
                </TableRow>
            );
        }
        return summary.map((row, index) => {
            const pkg = row[pkgKey] || {};
            const panels = row.panelStats || {};
            const hwItems = row.hardwareItemStats || {};
            return (
                <TableRow key={`${row.projectNo}-${index}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.projectNo || '-'}</TableCell>
                    <TableCell>{row.customerName || '-'}</TableCell>
                    {isModular ? (
                        renderPartialPanelStatsCells(row, panels)
                    ) : (
                        <>
                            <TableCell align="center">
                                {renderPackageCount(pkg.totalPackages, row, label, 'all')}
                            </TableCell>
                            <TableCell align="center">
                                {renderPackageCount(pkg.deliveredPackages, row, label, 'delivered')}
                            </TableCell>
                            <TableCell align="center">
                                {renderHardwareItemCount(hwItems.totalHardwareItems, row, 'all')}
                            </TableCell>
                            <TableCell align="center">
                                {renderHardwareItemCount(hwItems.pendingHardwareItems, row, 'pending')}
                            </TableCell>
                            <TableCell align="center">
                                {formatDate(getHardwareDeliveredDate(pkg))}
                            </TableCell>
                        </>
                    )}
                </TableRow>
            );
        });
    };

    const renderBothTable = () => (
        <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <StyledTableCell rowSpan={2} align="center">S.No</StyledTableCell>
                        <StyledTableCell rowSpan={2} align="left">Project No</StyledTableCell>
                        <StyledTableCell rowSpan={2} align="left">Customer Name</StyledTableCell>
                        {showDeliveryTypeColumn && (
                            <StyledTableCell rowSpan={2} align="center">Delivery Type</StyledTableCell>
                        )}
                        <StyledTableCell align="center" colSpan={modularColumnCount}>Modular</StyledTableCell>
                        {showHardwareSection && (
                            <StyledTableCell align="center" colSpan={5}>Hardware</StyledTableCell>
                        )}
                    </TableRow>
                    <TableRow>
                        {renderPartialPanelStatsHeader()}
                        {showHardwareSection && (
                            <>
                                <StyledTableCell align="center">Total Packages</StyledTableCell>
                                <StyledTableCell align="center">Delivered Packages</StyledTableCell>
                                <StyledTableCell align="center">Total Items</StyledTableCell>
                                <StyledTableCell align="center">Undelivered Items</StyledTableCell>
                                <DeliveredDateHeaderCell />
                            </>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!summary.length ? (
                        <TableRow>
                            <StyledTableCell colSpan={bothTypeColSpan} align="center">No records found</StyledTableCell>
                        </TableRow>
                    ) : (
                        summary.map((row, index) => {
                            const hardware = row.hardwarePackages || {};
                            const panels = row.panelStats || {};
                            const hwItems = row.hardwareItemStats || {};
                            return (
                                <TableRow
                                    key={`${row._deliverySource || mode}-${row.projectNo}-${index}`}
                                    className={`${classes.tableRowHover} ${index % 2 === 0 ? classes.tableRowEven : ''}`}
                                >
                                    <StyledTableCell align="center">{index + 1}</StyledTableCell>
                                    <StyledTableCell align="left">{row.projectNo || '-'}</StyledTableCell>
                                    <StyledTableCell align="left">{row.customerName || '-'}</StyledTableCell>
                                    {showDeliveryTypeColumn && (
                                        <StyledTableCell align="center">
                                            <Chip
                                                size="small"
                                                label={getDeliveryTypeLabel(row)}
                                                className={`${classes.deliveryTypeChip} ${
                                                    row._deliverySource === 'full'
                                                        ? classes.deliveredChip
                                                        : classes.partialDeliveryChip
                                                }`}
                                            />
                                        </StyledTableCell>
                                    )}
                                    {renderPartialPanelStatsCells(row, panels)}
                                    {showHardwareSection && (
                                        <>
                                            <StyledTableCell align="center">
                                                {renderPackageCount(hardware.totalPackages, row, PACKAGE_TYPE.HARDWARE, 'all')}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {renderPackageCount(hardware.deliveredPackages, row, PACKAGE_TYPE.HARDWARE, 'delivered')}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {renderHardwareItemCount(hwItems.totalHardwareItems, row, 'all')}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {renderHardwareItemCount(hwItems.pendingHardwareItems, row, 'pending')}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">{formatDate(getHardwareDeliveredDate(hardware))}</StyledTableCell>
                                        </>
                                    )}
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
                {summary.length > 0 && (
                    <TableFooter>
                        {renderSummaryTotalsRow()}
                    </TableFooter>
                )}
            </Table>
        </TableContainer>
    );

    return (
        <div className={embedded ? classes.rootEmbedded : classes.root}>
            {!hidePageTitle && (
                <Box className={classes.pageHeader}>
                    <Typography variant="h5" className={classes.heading}>
                        {pageConfig.title}
                    </Typography>
                </Box>
            )}

            <Paper className={classes.filterPaper} elevation={0}>
                <Box className={classes.filterLayout}>
                    <TextField
                        className={classes.filterSearch}
                        label="Project No / Customer Name"
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSearchSubmit();
                        }}
                        placeholder="Press Enter to search"
                    />

                    <Box className={classes.filterDatesGroup}>
                        <TextField
                            className={classes.dateField}
                            label="Start Date"
                            type="date"
                            size="small"
                            variant="outlined"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            className={classes.dateField}
                            label="End Date"
                            type="date"
                            size="small"
                            variant="outlined"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>

                    <Box className={classes.filterActions}>
                        <Button
                            className={classes.submitButton}
                            variant="contained"
                            size="small"
                            onClick={handleDateSubmit}
                        >
                            Submit
                        </Button>
                        <Button
                            className={classes.clearButton}
                            variant="contained"
                            size="small"
                            onClick={handleClearFilter}
                        >
                            Clear
                        </Button>
                        <Button
                            className={isFetchDisabled ? classes.disabledFetchButton : classes.fetchNewButton}
                            variant="contained"
                            size="small"
                            onClick={handleFetchNewData}
                            disabled={isFetchDisabled}
                        >
                            {isFetchDisabled ? `Wait (${formatFetchTimeLeft()})` : 'Fetch New Data'}
                        </Button>
                        <Tooltip title={`Download all ${totalRecords} table records as Excel`}>
                            <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    className={classes.submitButton}
                                    startIcon={<GetAppIcon fontSize="small" />}
                                    disabled={downloadingAllData || !totalRecords}
                                    onClick={handleDownloadAllTableData}
                                >
                                    Download All Data
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>

                    <Box className={classes.filterRightGroup}>
                        <Typography className={classes.totalRecords} component="span">
                            Total Records: <strong>{totalRecords}</strong>
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Paper className={classes.tableSection} elevation={0}>
                <Box className={classes.tableToolbar}>
                    <Typography className={classes.tableToolbarTitle}>
                        Delivery Records
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Showing <strong>{summary.length}</strong> of <strong>{totalRecords}</strong> records
                        {loadingMore && ' • Loading more...'}
                        {!loadingMore && nextPage && ' • Scroll for more'}
                    </Typography>
                </Box>

                {renderBothTable()}

                <div ref={loadMoreSentinelRef} className={classes.loadMoreSentinel} />
                {loadingMore && (
                    <Box className={classes.pagination}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="textSecondary">
                            Loading more records...
                        </Typography>
                    </Box>
                )}
            </Paper>

            <Dialog open={packageModalOpen} onClose={closePackageModal} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {packageMeta?.type} {getPackageFilterLabel(packageMeta?.filter)} Packages
                    {packageMeta?.project && (
                        <Typography variant="body2" color="textSecondary">
                            {packageMeta.project.projectNo} | {packageMeta.project.customerName}
                            {' '}| {getPackageFilterLabel(packageMeta?.filter)} Packages: {packageMeta.totalPackages}
                        </Typography>
                    )}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {modalLoading ? (
                        <LoadingOverlay label="Loading packages..." />
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>S.No</StyledTableCell>
                                        <StyledTableCell>Package Name</StyledTableCell>
                                        {!isHardwareModal && <StyledTableCell>Panel Name</StyledTableCell>}
                                        {!isHardwareModal && <StyledTableCell>Box ID</StyledTableCell>}
                                        <StyledTableCell align="center">Status</StyledTableCell>
                                        <StyledTableCell align="center">Shipping Time</StyledTableCell>
                                        <StyledTableCell align="center">Total Items</StyledTableCell>
                                        <StyledTableCell align="center">View</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!visibleModalPackages.length ? (
                                        <TableRow>
                                            <TableCell colSpan={isHardwareModal ? 6 : 8} align="center">No packages found</TableCell>
                                        </TableRow>
                                    ) : (
                                        visibleModalPackages.map((pkg, index) => (
                                            <TableRow key={`${pkg.packageName || 'pkg'}-${index}`}>
                                                <TableCell>{getSerialNumber(index, packageModalPage, DEFAULT_PAGE_SIZE)}</TableCell>
                                                <TableCell>{pkg.packageName || '-'}</TableCell>
                                                {!isHardwareModal && <TableCell>{getPackageItemNames(pkg, false)}</TableCell>}
                                                {!isHardwareModal && <TableCell>{pkg.boxId || '-'}</TableCell>}
                                                <TableCell align="center">
                                                    <Chip
                                                        size="small"
                                                        label={getPackageStatusLabel(pkg.status)}
                                                        className={getPackageStatusChipClass(pkg.status, classes)}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">{formatDate(pkg.shippingTime)}</TableCell>
                                                <TableCell align="center">{pkg.totalItems ?? (pkg.items ? pkg.items.length : 0)}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title={isHardwareModal ? 'View item details' : 'View panel details'}>
                                                        <IconButton size="small" color="primary" onClick={() => openItemsPopup(pkg)}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {!modalLoading && (packageModalTotalPages > 1 || packageModalNextPage) && (
                        <div className={classes.pagination}>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={packageModalPage <= 1}
                                onClick={() => handlePackageModalPageChange(packageModalPage - 1)}
                            >
                                Previous
                            </Button>
                            <Typography variant="body2">
                                Page {packageModalPage} of {packageModalTotalPages}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!packageModalNextPage}
                                onClick={() => handlePackageModalPageChange(packageModalPage + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    {packageModalContext?.row && (
                        <Button
                            variant="contained"
                            className={classes.submitButton}
                            startIcon={<GetAppIcon />}
                            disabled={
                                isModalDownloadLoading(packageModalContext.row.projectNo)
                                || isRowDownloadLoading(packageModalContext.row.projectNo)
                            }
                            onClick={handleDownloadModalPackages}
                        >
                            Download {packageMeta?.totalPackages || ''} {getPackageFilterLabel(packageModalContext.filter)}
                        </Button>
                    )}
                    <Button onClick={closePackageModal} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={itemsPopupOpen} onClose={closeItemsPopup} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {isHardwareModal ? 'Item Details' : 'Panel Details'}
                    {selectedPackage && (
                        <Typography variant="body2" color="textSecondary">
                            {selectedPackage.packageName}
                            {!isHardwareModal && selectedPackage.boxId ? ` | Box: ${selectedPackage.boxId}` : ''}
                            {' '}| Items: {selectedPackage.totalItems ?? (selectedPackage.items ? selectedPackage.items.length : 0)}
                        </Typography>
                    )}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                {isHardwareModal ? (
                                    <TableRow>
                                        <StyledTableCell>S.No</StyledTableCell>
                                        <StyledTableCell>Item Name</StyledTableCell>
                                        <StyledTableCell align="center">Quantity</StyledTableCell>
                                        <StyledTableCell align="center">Delivered Qty</StyledTableCell>
                                        <StyledTableCell align="center">Stage</StyledTableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow>
                                        <StyledTableCell>S.No</StyledTableCell>
                                        <StyledTableCell>Panel Name</StyledTableCell>
                                        <StyledTableCell>Material</StyledTableCell>
                                        <StyledTableCell>Barcode</StyledTableCell>
                                        <StyledTableCell>CP90 Name</StyledTableCell>
                                        <StyledTableCell>Auftrag</StyledTableCell>
                                        <StyledTableCell align="center">Length</StyledTableCell>
                                        <StyledTableCell align="center">Width</StyledTableCell>
                                        <StyledTableCell align="center">Thickness</StyledTableCell>
                                        <StyledTableCell>Space Type</StyledTableCell>
                                        <StyledTableCell align="center">Stage</StyledTableCell>
                                    </TableRow>
                                )}
                            </TableHead>
                            <TableBody>
                                {!(selectedPackage && selectedPackage.items && selectedPackage.items.length) ? (
                                    <TableRow>
                                        <TableCell colSpan={isHardwareModal ? 5 : 11} align="center">
                                            {isHardwareModal ? 'No item details found' : 'No panel details found'}
                                        </TableCell>
                                    </TableRow>
                                ) : isHardwareModal ? (
                                    selectedPackage.items.map((item, index) => (
                                        <TableRow key={item._id || index}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{getItemDisplayName(item, true)}</TableCell>
                                            <TableCell align="center">{item.quantity ?? '-'}</TableCell>
                                            <TableCell align="center">{item.suppliedQuantity ?? '-'}</TableCell>
                                            <TableCell align="center">
                                                <Chip size="small" label={getPanelStageLabel(item.stage, item)} className={getPanelStageChipClass(item, classes)} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    selectedPackage.items.map((item, index) => (
                                        <TableRow key={item._id || index}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{getItemDisplayName(item, false)}</TableCell>
                                            <TableCell>{item.matname || '-'}</TableCell>
                                            <TableCell>{item.barcode || '-'}</TableCell>
                                            <TableCell>{item.cp90name || '-'}</TableCell>
                                            <TableCell>{item.auftrag || '-'}</TableCell>
                                            <TableCell align="center">{item.rleng ?? '-'}</TableCell>
                                            <TableCell align="center">{item.rwidth ?? '-'}</TableCell>
                                            <TableCell align="center">{item.rthk ?? '-'}</TableCell>
                                            <TableCell>{item.space_type || '-'}</TableCell>
                                            <TableCell align="center">
                                                <Chip size="small" label={getPanelStageLabel(item.stage, item)} className={getPanelStageChipClass(item, classes)} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeItemsPopup} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Hardware Item Details Modal */}
            <Dialog open={hwItemModalOpen} onClose={closeHwItemModal} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {hwItemModalMeta?.filter === 'pending' ? 'Undelivered Hardware Items' : 'All Hardware Items'}
                    {hwItemModalMeta?.project && (
                        <Typography variant="body2" color="textSecondary">
                            {hwItemModalMeta.project.projectNo} | {hwItemModalMeta.project.customerName}
                            {' '}| Total: {hwItemModalMeta.totalItems}
                        </Typography>
                    )}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {hwItemModalLoading ? (
                        <LoadingOverlay label="Loading hardware items..." />
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>S.No</StyledTableCell>
                                        <StyledTableCell>Item Name</StyledTableCell>
                                        <StyledTableCell align="center">Quantity</StyledTableCell>
                                        <StyledTableCell align="center">Qty Type</StyledTableCell>
                                        <StyledTableCell align="center">Delivered Qty</StyledTableCell>
                                        <StyledTableCell align="center">Undelivered Qty</StyledTableCell>
                                        <StyledTableCell align="center">Stage</StyledTableCell>
                                        <StyledTableCell>Remarks</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!hwItemModalData.length ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">No hardware items found</TableCell>
                                        </TableRow>
                                    ) : (
                                        hwItemModalData.map((item, index) => (
                                            <TableRow key={item._id || index}>
                                                <TableCell>
                                                    {getSerialNumber(index, hwItemModalPage, DEFAULT_PAGE_SIZE)}
                                                </TableCell>
                                                <TableCell>{item.name || '-'}</TableCell>
                                                <TableCell align="center">{item.quantity ?? '-'}</TableCell>
                                                <TableCell align="center">{item.quantityType || '-'}</TableCell>
                                                <TableCell align="center">{item.suppliedQuantity ?? '-'}</TableCell>
                                                <TableCell align="center">{item.pendingQuantity ?? '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        size="small"
                                                        label={getPanelStageLabel(item.stage, item)}
                                                        className={getPanelStageChipClass(item, classes)}
                                                    />
                                                </TableCell>
                                                <TableCell>{item.remarks || '-'}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {!hwItemModalLoading && (hwItemModalNextPage || hwItemModalPage > 1) && (
                        <div className={classes.pagination}>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={hwItemModalPage <= 1}
                                onClick={() => handleHwItemModalPageChange(hwItemModalPage - 1)}
                            >
                                Previous
                            </Button>
                            <Typography variant="body2">Page {hwItemModalPage}</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!hwItemModalNextPage}
                                onClick={() => handleHwItemModalPageChange(hwItemModalPage + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        className={classes.submitButton}
                        startIcon={<GetAppIcon />}
                        disabled={downloadingHwItemModal || !hwItemModalData.length}
                        onClick={handleDownloadHwItemModalData}
                    >
                        Download {hwItemModalMeta?.totalItems || ''} {hwItemModalMeta?.filter === 'pending' ? 'Undelivered' : 'All'} Items
                    </Button>
                    <Button onClick={closeHwItemModal} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Panel Details Modal */}
            <Dialog open={panelModalOpen} onClose={closePanelModal} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {getPanelFilterLabel(panelModalMeta?.filter)} Panels
                    {panelModalMeta?.project && (
                        <Typography variant="body2" color="textSecondary">
                            {panelModalMeta.project.projectNo} | {panelModalMeta.project.customerName}
                            {' '}| Total: {(panelModalMeta.count || panelModalMeta.totalPanels || 0).toLocaleString()}
                        </Typography>
                    )}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    {panelModalMeta?.isPreview && (
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                            Showing first <strong>{panelModalMeta.previewCount?.toLocaleString()}</strong> panel records in table.
                            {' '}
                            Use download for all {(panelModalMeta.count || panelModalMeta.totalPanels || 0).toLocaleString()} records.
                        </Typography>
                    )}
                    {panelModalLoading ? (
                        <LoadingOverlay label="Loading panels..." />
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>S.No</StyledTableCell>
                                        {panelModalContext?.isAggregate && (
                                            <>
                                                <StyledTableCell>Project No</StyledTableCell>
                                                <StyledTableCell>Customer Name</StyledTableCell>
                                            </>
                                        )}
                                        <StyledTableCell>Panel Name</StyledTableCell>
                                        <StyledTableCell>Material</StyledTableCell>
                                        <StyledTableCell>Barcode</StyledTableCell>
                                        <StyledTableCell>CP90 Name</StyledTableCell>
                                        <StyledTableCell>Auftrag</StyledTableCell>
                                        <StyledTableCell align="center">Length</StyledTableCell>
                                        <StyledTableCell align="center">Width</StyledTableCell>
                                        <StyledTableCell align="center">Thickness</StyledTableCell>
                                        <StyledTableCell>Space Type</StyledTableCell>
                                        <StyledTableCell align="center">Stage</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!panelModalData.length ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={panelModalContext?.isAggregate ? 13 : 11}
                                                align="center"
                                            >
                                                No panels found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        panelModalData.map((panel, index) => (
                                            <TableRow key={panel._id || `${panel.projectNo || 'panel'}-${index}`}>
                                                <TableCell>
                                                    {panelModalContext?.isAggregate
                                                        ? index + 1
                                                        : getSerialNumber(index, panelModalPage, DEFAULT_PAGE_SIZE)}
                                                </TableCell>
                                                {panelModalContext?.isAggregate && (
                                                    <>
                                                        <TableCell>{panel.projectNo || '-'}</TableCell>
                                                        <TableCell>{panel.customerName || '-'}</TableCell>
                                                    </>
                                                )}
                                                <TableCell>{panel.name1 || panel.name || '-'}</TableCell>
                                                <TableCell>{panel.matname || '-'}</TableCell>
                                                <TableCell>{panel.barcode || '-'}</TableCell>
                                                <TableCell>{panel.cp90name || '-'}</TableCell>
                                                <TableCell>{panel.auftrag || '-'}</TableCell>
                                                <TableCell align="center">{panel.rleng ?? '-'}</TableCell>
                                                <TableCell align="center">{panel.rwidth ?? '-'}</TableCell>
                                                <TableCell align="center">{panel.rthk ?? '-'}</TableCell>
                                                <TableCell>{panel.space_type || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        size="small"
                                                        label={getPanelStageLabel(panel.stage, panel)}
                                                        className={getPanelStageChipClass(panel, classes)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {!panelModalLoading && !panelModalContext?.isAggregate && (panelModalNextPage || panelModalPage > 1) && (
                        <div className={classes.pagination}>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={panelModalPage <= 1}
                                onClick={() => handlePanelModalPageChange(panelModalPage - 1)}
                            >
                                Previous
                            </Button>
                            <Typography variant="body2">Page {panelModalPage}</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!panelModalNextPage}
                                onClick={() => handlePanelModalPageChange(panelModalPage + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        className={classes.submitButton}
                        startIcon={<GetAppIcon />}
                        disabled={panelDownloadOpen || !panelModalData.length}
                        onClick={handleDownloadPanelModalData}
                    >
                        {panelModalMeta?.isPreview
                            ? `Download All CSV (${(panelModalMeta?.count || panelModalMeta?.totalPanels || 0).toLocaleString()})`
                            : `Download CSV (${(panelModalMeta?.totalPanels || panelModalMeta?.count || 0).toLocaleString()})`}
                    </Button>
                    <Button onClick={closePanelModal} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>

            <PanelDownloadProgressDialog
                open={panelDownloadOpen}
                progress={panelDownloadProgress}
                totalCount={panelModalMeta?.count || panelModalMeta?.totalPanels || 0}
            />

            {/*
            Download options dialog removed — Download All Data exports the rendered table only.
            <Dialog open={downloadDialogOpen} ...>...</Dialog>
            */}
        </div>
    );
};

const FullPackageDelivered = () => (
    <Redirect to="/panel-delivery-reports?tab=full" />
);

export default FullPackageDelivered;
